<template>
  <section v-if="supported && uid" class="reminder">
    <div class="reminder__row">
      <span class="reminder__label">Remind me daily</span>
      <input v-if="on" v-model="time" class="reminder__time" type="time" step="900" :disabled="busy" aria-label="Reminder time" @change="saveTime">
      <button type="button" class="reminder__switch" role="switch" :aria-checked="on" :disabled="busy" @click="toggle">
        {{ on ? 'On' : 'Off' }}
      </button>
    </div>
    <p v-if="note" class="reminder__note muted">{{ note }}</p>
  </section>
</template>

<script>
// "Remind me daily" (2026-10-11, Matt: "I would like a optional notification
// to remind me to do the crossword every day").
//
// The same plumbing as the games' TurnBell (camel-up et al.): this phone's
// push subscription is saved at atrPush/<uid>/subscriptions/<id>, tagged
// 'crossword', with `remindAt` ("HH:MM") and `tz`. The hub's atr-push Lambda
// (around-table-round/aws-lambda/atr-push.js, `sweepCrossword`) sends
// "Today's crossword: <title>" once that time has passed, at most once a
// day, and only if there's no crosswordTimes row for you today (unsolved).
// public/push-sw.js (the house push worker) is registered on its own scope
// so it never replaces the app's real service worker.
import { ref as dbRef, get, set, update } from 'firebase/database';
import { db } from '@/firebase';

// The atr-push Lambda's VAPID public key — public by design, same as the hub's.
const VAPID_PUBLIC_KEY = 'BPxOUP62DQBAJXpY2Zw2W01rbSAotteS1LH0gVCkAfVQsbzdFrqpVRAN6BSZDXbZht710R1VbyC-wNYLDQ7V0ng';
const SW_URL = '/push-sw.js';
const SW_SCOPE = '/push/';
const DEFAULT_TIME = '08:00';

const pushSupported = () => typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
const urlBase64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};
const subscriptionId = (endpoint) => {
  let hash = 0;
  for (let i = 0; i < endpoint.length; i += 1) hash = ((hash << 5) - hash + endpoint.charCodeAt(i)) | 0;
  return `sub-${Math.abs(hash).toString(36)}`;
};
const timeZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'; } catch { return 'America/New_York'; }
};

export default {
  name: 'ReminderBell',
  data() { return { supported: pushSupported(), on: false, busy: false, note: '', time: DEFAULT_TIME }; },
  computed: { uid() { return this.$store.state.user?.uid || null; } },
  watch: { uid() { this.refresh(); } },
  mounted() {
    this.refresh();
    // A reminder still sitting in Notification Center goes once you're here.
    this.closeDelivered();
  },
  methods: {
    async closeDelivered() {
      if (!this.supported) return;
      try {
        const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
        for (const n of (await reg?.getNotifications?.()) || []) n.close();
      } catch { /* they stay until swiped */ }
    },
    async registration() {
      return (await navigator.serviceWorker.getRegistration(SW_SCOPE)) || navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
    },
    async current() {
      try { return (await (await this.registration()).pushManager.getSubscription()) || null; } catch { return null; }
    },
    path(sub) { return `atrPush/${this.uid}/subscriptions/${subscriptionId(sub.endpoint)}`; },
    // On = this phone has a subscription AND it is saved as a crossword one.
    async refresh() {
      if (!this.supported || !this.uid || !db) return;
      const sub = await this.current();
      if (!sub) { this.on = false; return; }
      const row = (await get(dbRef(db, this.path(sub))).catch(() => null))?.val();
      this.on = Boolean(row && row.game === 'crossword' && row.remindAt);
      if (this.on) this.time = row.remindAt;
    },
    async toggle() {
      this.busy = true; this.note = '';
      try {
        if (this.on) await this.unsubscribe(); else await this.subscribe();
      } catch (e) { this.note = e.message; } finally { this.busy = false; await this.refresh(); }
    },
    async subscribe() {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;
      if (isIOS && !standalone) throw new Error('On iPhone, add Crossword to your Home Screen (from Safari) first — reminders only work in the installed app.');
      if ((await Notification.requestPermission()) !== 'granted') throw new Error('Notifications are blocked in this browser’s settings.');
      const reg = await this.registration();
      await navigator.serviceWorker.ready.catch(() => {});
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) });
      const json = sub.toJSON();
      await set(dbRef(db, this.path(json)), {
        endpoint: json.endpoint, keys: json.keys, ua: navigator.userAgent, savedAt: Date.now(),
        game: 'crossword', remindAt: this.time || DEFAULT_TIME, tz: timeZone(),
      });
      this.note = `On. A test notification is on its way; after that, ${this.label()} each day if you haven't solved it yet.`;
    },
    async saveTime() {
      if (!this.on || !/^\d{2}:\d{2}$/.test(this.time)) return;
      const sub = await this.current();
      if (!sub) return;
      await update(dbRef(db, this.path(sub)), { remindAt: this.time, tz: timeZone(), savedAt: Date.now() }).catch(() => {});
      this.note = `Reminders at ${this.label()}.`;
    },
    async unsubscribe() {
      const sub = await this.current();
      if (!sub) return;
      const path = this.path(sub);
      await sub.unsubscribe().catch(() => {});
      await set(dbRef(db, path), null);
      this.note = '';
    },
    label() {
      const [h, m] = this.time.split(':').map(Number);
      return new Date(2000, 0, 1, h, m).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    },
  },
};
</script>

<style lang="scss" scoped>
.reminder { border: 1px solid var(--edge); border-radius: var(--radius); background: var(--surface); padding: 0.6rem 0.8rem; }
.reminder__row { display: flex; align-items: center; gap: 0.6rem; }
.reminder__label { flex: 1; font-size: 0.9rem; }
.reminder__time { flex: 0 0 auto; width: auto; font-size: 0.9rem; min-height: 40px; padding: 0 0.4rem; }
.reminder__switch {
  flex: 0 0 auto; width: auto; min-width: 3.4rem; padding: 0 0.8rem; font-size: 0.85rem; font-weight: 700;
  border-radius: 999px; border: 1px solid var(--edge); background: transparent; color: var(--ink-dim);
  &[aria-checked='true'] { background: var(--accent); border-color: var(--accent); color: var(--felt-deep); }
}
.reminder__note { margin: 0.4rem 0 0; font-size: 0.8rem; }
</style>
